/**
 * Global catalog of user-uploaded creatures and plants. Lives at
 * /api/catalog as a single JSON blob. Each world references catalog
 * entries by id via `worldEntries` — enable + count are per-world, all
 * other config (orientation, scale, behaviour, GLB url) comes from here.
 */

export interface CatalogCreature {
  id: string;
  label: string;
  glbUrl: string;
  /** Default model scale (world.worldEntries count overrides population only — not scale). */
  scale: number;
  /** Baked-in orientation tweak (radians) — fixes wrong-facing GLBs. */
  rotX: number;
  rotY: number;
  rotZ: number;
  /** Extra Y offset in metres after auto-levelling. */
  offY: number;
  /** Locomotion speed in m/s. */
  speed: number;
  /** Default behaviour variant. */
  behavior: 'peaceful' | 'predator' | 'prey';
  /** Hit points (predators only really use this). */
  hp: number;
  /** Bite damage-per-second (predator only). */
  bitePerSec: number;
  /** Default count used when a world first adds this creature. */
  defaultCount: number;
  /** If set, this entry uses the specialised in-game sim code for the
   *  named system (SpiderSim / GrasshopperSim / LizardSim / LawnMower /
   *  RolyPolySim). Plain entries use the generic CustomCreatureSim. */
  systemSim?: 'spider' | 'grasshopper' | 'rolyPoly' | 'lizard' | 'mower';
  /** Global /api/tune key for this entry's renderer (e.g. 'grasshopper',
   *  'wolf-spider'). System entries point at their hard-coded renderer
   *  tune ids; uploads leave this undefined (they use the catalog's own
   *  stored transform instead of the tune store). */
  tuneKey?: string;
}

export interface CatalogPlant {
  id: string;
  label: string;
  glbUrl: string;
  scaleMin: number;
  scaleMax: number;
  rotX: number;
  rotY: number;
  rotZ: number;
  offY: number;
  alignTerrain: boolean;
  /** Default count used when a world first adds this plant. */
  defaultCount: number;
  /** If set, this plant uses the specialised in-game renderer (grass / rocks). */
  systemPlant?: 'grass' | 'rocks';
  /** Global /api/tune key for the renderer — e.g. 'grass-tuft', 'rocks-stylised'. */
  tuneKey?: string;
  /** If true, treat a multi-mesh GLB as a *variant pack* — each instance
   *  picks a random mesh from the scene. Set on asset packs like
   *  "desert stones" / "desert shrubs". Leave false for compound objects
   *  like trees. */
  isPack?: boolean;
}

export interface Catalog {
  creatures: CatalogCreature[];
  plants: CatalogPlant[];
}

export function emptyCatalog(): Catalog {
  return { creatures: [], plants: [] };
}

/** Seed entries representing the "built-in" systems (spider, grasshopper,
 *  roly-poly, lizard, lawn mower, grass, rocks). Each is a normal catalog
 *  entry — what makes them special is the `systemSim` / `systemPlant` tag
 *  telling the game runtime to route them through the dedicated simulator
 *  instead of the generic CustomCreatureSim path. */
export function seededCatalog(): Catalog {
  return {
    creatures: [
      { id: 'sys-spider',      label: 'Spider',      glbUrl: '/assets/wolf-spider.glb',    scale: 1, rotX: 0, rotY: 0, rotZ: 0, offY: 0, speed: 0.6, behavior: 'predator', hp: 60,   bitePerSec: 0,    defaultCount: 2, systemSim: 'spider',      tuneKey: 'wolf-spider'  },
      { id: 'sys-grasshopper', label: 'Grasshopper', glbUrl: '/assets/grasshopper-v6.glb', scale: 1, rotX: 0, rotY: 0, rotZ: 0, offY: 0, speed: 1.2, behavior: 'prey',     hp: 10,   bitePerSec: 0,    defaultCount: 2, systemSim: 'grasshopper', tuneKey: 'grasshopper'  },
      { id: 'sys-rolyPoly',    label: 'Roly-poly',   glbUrl: '/assets/roly-poly.glb',      scale: 1, rotX: 0, rotY: 0, rotZ: 0, offY: 0, speed: 0.3, behavior: 'peaceful', hp: 999,  bitePerSec: 0,    defaultCount: 5, systemSim: 'rolyPoly',    tuneKey: 'roly-poly'    },
      { id: 'sys-lizard',      label: 'Lizard',      glbUrl: '/assets/lizard.glb',         scale: 1, rotX: 0, rotY: 0, rotZ: 0, offY: 0, speed: 2.8, behavior: 'predator', hp: 60,   bitePerSec: 0.5, defaultCount: 1, systemSim: 'lizard',      tuneKey: 'lizard'       },
      { id: 'sys-mower',       label: 'Lawn Mower',  glbUrl: '/assets/lawn-mower.glb',     scale: 1, rotX: 0, rotY: 0, rotZ: 0, offY: 0, speed: 1.5, behavior: 'predator', hp: 9999, bitePerSec: 5,   defaultCount: 1, systemSim: 'mower',       tuneKey: 'lawn-mower'   },
    ],
    plants: [
      { id: 'sys-grass', label: 'Grass', glbUrl: '/assets/grass-tuft.glb',     scaleMin: 0.09, scaleMax: 0.11, rotX: 0, rotY: 0, rotZ: 0, offY: 0, alignTerrain: true,  defaultCount: 4000, systemPlant: 'grass', tuneKey: 'grass-tuft'     },
      { id: 'sys-rocks', label: 'Rocks', glbUrl: '/assets/lowpoly-rocks.glb', scaleMin: 0.8,  scaleMax: 1.2,  rotX: 0, rotY: 0, rotZ: 0, offY: 0, alignTerrain: false, defaultCount: 200,  systemPlant: 'rocks', tuneKey: 'rocks-stylised' },
    ],
  };
}

const API_URL = '/api/catalog';

let cache: Catalog = emptyCatalog();

export async function loadCatalog(): Promise<Catalog> {
  try {
    const res = await fetch(API_URL, { cache: 'no-cache' });
    if (!res.ok) return cache;
    const j = await res.json();
    if (j && typeof j === 'object') {
      cache = {
        creatures: Array.isArray(j.creatures) ? j.creatures : [],
        plants:    Array.isArray(j.plants)    ? j.plants    : [],
      };
    }
  } catch (err) {
    console.warn('loadCatalog failed:', err);
  }
  return cache;
}

export async function saveCatalog(c: Catalog): Promise<void> {
  cache = c;
  const res = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(c),
  });
  if (!res.ok) throw new Error(`POST ${API_URL} → ${res.status}`);
}

export function getCachedCatalog(): Catalog { return cache; }

/** Generate a fresh catalog-safe id from a name. */
export function makeCatalogId(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
  const suffix = Math.random().toString(36).slice(2, 6);
  return (slug || 'item') + '-' + suffix;
}
